import { render } from 'preact';
import type { ErrorParam } from '@schibsted-svp/web-player';

import type {
  PlayerContext,
  PlayerConfigOptions,
  Plugin,
  RegisteredPlugin,
  PaywallMessagePluginOptions,
  PlayerConfigPaywall,
} from '../../types/global';
import { injectStyles } from '../utils';
import { SchibstedAccount } from '../../config';
import { UtmParameters } from '../../utils/getUtmParams';
import { getRawAsset } from '../../utils/player/asset';
import { areAirplayAdsEnabled } from '../../utils/airplayAds';

import { Skin } from './Skin';
import { isAudioContent } from './utils/isAudioContent';
import { ResolvedExperiment } from './hooks/ExperimentsProvider';
import { Config } from './hooks/ConfigProvider';
import { firePixelTrackers } from './utils/pixelTracker';
import { resolveTitleOverride } from './utils/titleOverride';

import styles from './index.inline.scss';

export interface VgtvSkinConfig {
  dnaStyle?: PlayerConfigOptions['dnaStyle'];
  schibstedAccount: SchibstedAccount;
  onLoginClick: () => void;
  context: PlayerContext;
  recommended: {
    autoplay: boolean;
  };
  experiments?: ResolvedExperiment[];
  ui?: Config['ui'];
  floating?: Config['floating'];
  podcast?: Config['podcast'];
  utmParameters?: PaywallMessagePluginOptions['utmParameters'];
  getSubscriptionUrl?: PlayerConfigPaywall['getSubscriptionUrl'];
  playlist?: Config['playlist'];
}

export interface VgtvSkinPlugin extends Plugin<
  VgtvSkinPlugin,
  { options: VgtvSkinConfig }
> {
  options: VgtvSkinConfig;

  render(
    this: RegisteredPlugin<VgtvSkinPlugin>,
    options?: { error?: ErrorParam; cast?: jwplayer.CastParam }
  ): void;
  getInitialError(
    this: RegisteredPlugin<VgtvSkinPlugin>
  ): ErrorParam | undefined;
  unmount(this: RegisteredPlugin<VgtvSkinPlugin>): void;
  getPlayerContainer(
    this: RegisteredPlugin<VgtvSkinPlugin>
  ): HTMLElement | null;
  getSkinContainer(this: RegisteredPlugin<VgtvSkinPlugin>): HTMLElement | null;
  fixTextTracks(this: RegisteredPlugin<VgtvSkinPlugin>): void;
  firePixelTrackers(this: RegisteredPlugin<VgtvSkinPlugin>): void;
  overrideSupportedVideoFormats(this: RegisteredPlugin<VgtvSkinPlugin>): void;
  patchPlaylistItemMetadata(this: RegisteredPlugin<VgtvSkinPlugin>): void;
}

const transformUtmParameters = (
  utmParameters: PaywallMessagePluginOptions['utmParameters']
): UtmParameters | undefined => {
  if (utmParameters) {
    return {
      utm_content: utmParameters.content,
      utm_source: utmParameters.source,
      utm_term: utmParameters.term,
      utm_medium: utmParameters.medium,
    };
  }

  return undefined;
};

const parseErrorDetailsFromDOM = (container: HTMLElement) => {
  const details: ErrorParam = {
    message: container.querySelector('.svp-player-message')!.childNodes[1]
      .nodeValue!,
  };

  const codeElement = container.querySelector<HTMLDivElement>(
    '.svp-player-error-code'
  );

  if (!codeElement) {
    return details;
  }

  const { code, referenceId } =
    codeElement.innerText.match(
      /(?<code>\d+)(, #(?<referenceId>[a-z0-9.]+))?$/i
    )?.groups || {};

  details.referenceId = referenceId;
  if (code) {
    details.code = parseInt(code, 10);
  }

  return details;
};

// Exported (in addition to being registered below) so the pre-'ready' error
// mounting behaviour in setup() can be exercised directly in tests without
// depending on the window.SVP.Player.Plugins.register mock, which returns a
// fresh object on every access in test/setup.js.
export const plugin: VgtvSkinPlugin = {
  options: {} as VgtvSkinConfig,

  initialize(config) {
    this.options = config.options;
  },

  getName() {
    return 'VgtvSkinPlugin';
  },

  setup() {
    const initialError = this.getInitialError();
    let hasRendered = false;

    this.player.once('ready', () => {
      const asset = getRawAsset(this.player);
      if (isAudioContent(asset) && this.player.getMute()) {
        this.player.setMute(false);
      }

      // Skip re-rendering if a pre-'ready' error already mounted the skin,
      // otherwise this would clobber the error payload with a clean render.
      if (!hasRendered) {
        hasRendered = true;
        this.render({ error: initialError });
      }

      this.overrideSupportedVideoFormats();

      // TODO: remove references to internal property player.model
      this.player.model.once('remove', () => this.unmount());
    });

    this.player.on('initialPlay', () => this.firePixelTrackers());
    this.player.on('playlistItem', () => this.fixTextTracks());
    this.player.on('playlistItem', () => this.patchPlaylistItemMetadata());

    // @ts-expect-error missing svp/web-player types
    this.player.on('cast', (cast: jwplayer.CastParam) => this.render({ cast }));

    // The player can abort playback config (e.g. entitlement/identity
    // failures) before 'ready' ever fires, in which case only 'error' is
    // emitted. Mount the skin from that error so the paywall/error UI can
    // take over instead of the native player's generic error banner.
    this.player.on('error', (error: ErrorParam) => {
      if (hasRendered) {
        return;
      }

      hasRendered = true;
      this.render({
        error: {
          code: error.code,
          referenceId: error.referenceId,
          message: error.details || error.message,
        },
      });
    });

    if (initialError) {
      hasRendered = true;
      this.render({ error: initialError });
    }
  },

  firePixelTrackers() {
    const asset = getRawAsset(this.player);

    if (asset) {
      firePixelTrackers(asset);
    }
  },

  async render({ error, cast } = {}) {
    const container = this.getSkinContainer();

    if (!container) {
      return;
    }

    injectStyles(styles, this.getName());

    render(
      <Skin
        player={this.player}
        initialError={error}
        castState={cast}
        schibstedAccount={this.options.schibstedAccount}
        paywall={{
          login: this.options.onLoginClick,
          utmParameters: transformUtmParameters(this.options.utmParameters),
          getSubscriptionUrl: this.options.getSubscriptionUrl,
        }}
        config={{
          dnaStyle: this.options.dnaStyle,
          context: this.options.context,
          recommended: this.options.recommended,
          experiments: this.options.experiments ?? [],
          ui: this.options.ui ?? {},
          playlist: this.options.playlist,
          floating: this.options.floating,
          podcast: this.options.podcast,
        }}
      />,
      container
    );
  },

  unmount() {
    const container = this.getSkinContainer();

    if (container) {
      render(null, container);
    }
  },

  getInitialError() {
    const container = this.getPlayerContainer();
    const hasError =
      !!container && container.classList.contains('svp-player-error');

    return hasError ? parseErrorDetailsFromDOM(container) : undefined;
  },

  overrideSupportedVideoFormats() {
    const UNSUPPORTED_VIDEO_FORMATS = ['webm'];

    const html5Provider = jwplayer.api.availableProviders.find(
      (e) => e.name === 'html5'
    );

    if (typeof html5Provider?.supports === 'function') {
      const originalSupportsFn = html5Provider.supports;

      html5Provider.supports = (...args) => {
        const { type = '' } = args[0] || {};
        let response = originalSupportsFn(...args);

        if (
          UNSUPPORTED_VIDEO_FORMATS.includes(type) &&
          areAirplayAdsEnabled()
        ) {
          response = false;
        }

        return response;
      };
    }
  },

  /**
   * JWPlayer has bug in it's html5 provider, that makes videos with initial seek (with a few exceptions) appear without captions control button.
   *
   * ref https://github.com/jwplayer/jwplayer/blob/master/src/js/providers/html5.ts
   *
   * .seek() sets `_canSeek` to `true` (ref https://github.com/jwplayer/jwplayer/blob/master/src/js/providers/html5.ts#L825)
   * which then blocks the event that is supposed to add HTMLMediaElement.textTracks listeners (ref https://github.com/jwplayer/jwplayer/blob/master/src/js/providers/html5.ts#L193)
   */
  fixTextTracks() {
    const jwplayer = this.player.getJWPlayer();
    if (!jwplayer) {
      return;
    }

    // the fix only applies to text tracks rendered natively
    if (jwplayer.getProvider().name !== 'html5') {
      return;
    }

    const playlistItem = jwplayer.getPlaylistItem(jwplayer.getPlaylistIndex())!;

    // if the playlist item contains ads, then the issue will fix itself (multiple .attachMedia() calls)
    if (Object.keys(playlistItem.adschedule || {}).length > 0) {
      return;
    }

    // no need to call `.attachMedia()` if item has no initial seek
    if (playlistItem.starttime && playlistItem.starttime > 0) {
      jwplayer.attachMedia();
    }
  },

  /**
   * Needed to set proper chromecast metadata. {@link createMediaInfo}
   */
  patchPlaylistItemMetadata() {
    const jwplayer = this.player.getJWPlayer();
    if (!jwplayer) {
      return;
    }
    const playlistItem = jwplayer.getPlaylistItem(0);
    const asset = getRawAsset(this.player);

    if (!playlistItem || !asset) {
      return;
    }

    playlistItem.title = this.options.ui?.hideTitle
      ? ''
      : (resolveTitleOverride(
          this.player.getRawConfig(),
          asset.id,
          this.options.ui?.titleOverride
        ) ?? asset.title);

    if (asset.series) {
      playlistItem.description = `${asset.category.title} · S${asset.series.seasonNumber}, E${asset.series.episodeNumber}`;
    } else {
      playlistItem.description = asset.category.title;
    }
  },

  /**
   * JW recreates the container on subsequent errors.
   * Since jwplayer may not be initialized at all, calls to player.getContainer() might throw
   */
  getPlayerContainer() {
    try {
      return this.player.getContainer();
    } catch {
      return document.getElementById(this.player.getRawConfig().node);
    }
  },

  /*
   * Fall back to main container in case the jw-wrapper is not present (possible cause: init error)
   */
  getSkinContainer() {
    const container = this.getPlayerContainer();
    return container?.querySelector<HTMLElement>('.jw-wrapper') || container;
  },
};

window.SVP.Player.Plugins.register(plugin);
